/**
 * Feature flag service for Aegix One.
 *
 * Fetches pre-computed feature flags via `/api/v2/feature/enabled`.
 */
import { client } from '../lib/http-client';
import type { FeatureCode } from '../types/features';

export interface SiteLicense {
  status: string;
  product: string;
  expires_at: string | null;
  grace_period_days: number;
  is_degraded: boolean;
  modules: Record<string, boolean>;
}

export interface SiteFeatures {
  site_id: number;
  site_public_id: string | null;
  site_name: string;
  organization_id: number | null;
  organization_name: string | null;
  features: Partial<Record<FeatureCode, boolean>>;
  license?: SiteLicense;
}

/**
 * Fetch pre-computed feature enabled states for user's sites.
 * Returns empty array on HTTP 403 (graceful permission fallback).
 * Throws on non-403 errors (e.g. 500 or network failure) so React Query can handle retries.
 */
export async function getFeatureEnabled(): Promise<SiteFeatures[]> {
  // The schema declares no error body for this endpoint, so `error` is
  // typed `never` and narrowing on it collapses the other destructured
  // bindings too — capture the response fields before the `if (error)`.
  const { data, error, response } = await client.GET(
    '/api/v2/feature/enabled',
    {},
  );
  const statusText = response?.statusText;
  if (response?.status === 403) {
    return [];
  }
  if (error) {
    throw new Error(`Failed to fetch features: ${statusText || 'API error'}`);
  }
  return (data as unknown as SiteFeatures[]) ?? [];
}
